import { defineStore } from 'pinia'
import Cookies from 'js-cookie'
import { useRuntimeConfig } from '#app'
import { SUPPORTED_CURRENCIES, currencyInfo } from '~/utils/currency'

type Account = {
  birthday: number,
  currency: string,
  email: string,
  first_name: string
  is_email_confirmed: boolean,
  language: string | null,
  last_name: string
  login: string
  phone: string | null,
  roles: string[]
}

type BillingInfo = {
  country_code: string
  address_line_1: string
  zip_code: string
  state_code: string
  address_line_2: string
  city: string
}

export const useAccountStore = defineStore('account', {
	state: () => {
        const config = useRuntimeConfig();
        return {
            name: 'Test' as string,
            contextId: '' as string,
            account: <Account | null>null,
            userBillingInfo: <BillingInfo | null> null,
            host: config.public.baseURL,
            // Shared table, not a local copy — the old duplicate rendered USD with a bare "$"
            // (ambiguous to Google's verifier: read as SGD/HKD on zh landings) and CNY with
            // JPY's "¥". utils/currency.ts is the single source of truth for symbols.
            currenciesList: SUPPORTED_CURRENCIES,
            currency: currencyInfo('USD')
        }
    },
	getters: {
		formattedName: (state) => `<${state.name}>`,
	},
	actions: {
		setName(value: string) {
			this.name = value
		},
		setContextId(value: string) {
			this.contextId = value
		},
    setAccount(value: Account | null) {
      this.account = value
    },
    setCurrency(value:{code: string, name: string, symbol: string}) {
      this.currency = value
    },
		logIn({email, password}: any) {
      const api = useApi()
			return api(`${this.host}/api/user/v1/auth/sign-in`, {
				method: 'POST',
        body: JSON.stringify({ email, password }),
          headers: {
            'X-TRIPENING-CONTEXT-ID': this.contextId,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': '',
          },
				onResponse({response }: any) {
          Cookies.set('auth', JSON.stringify(response._data))
				}
			})
		},
    signUp(payload: any) {
      const api = useApi()
      return api(`${this.host}/api/user/v1/registration`, {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: {
          'X-TRIPENING-CONTEXT-ID': this.contextId,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': '',
        },
        onResponse({response }: any) {
          Cookies.set('auth', JSON.stringify(response._data))
        }
      })
    },
    async fetchUser() {
      const api = useApi()
      return api(`${this.host}/api/user/v1/user`, {
        method: 'GET'
      }).then((res: any) => {
        this.account = res
      })
    },
    async updateAccount(payload:any) {
      const api = useApi()
      return api(`${this.host}/api/user/v1/user/update-profile`, {
        method: 'POST',
        body: JSON.stringify(payload),
      }).then((res:any) => {
        console.log(res)
      })
    },
    async fetchUserBillingInfo():Promise<BillingInfo> {
      const api = useApi()
      return api(`${this.host}/api/user/v1/user/billing-info`, {
        method: 'GET'
      }).then((res: any) => {
        this.userBillingInfo = res as BillingInfo
        return res
      })
    },
    changePassword(payload:any) {
      const api = useApi()
      return api(`${this.host}/api/user/v1/user/change-password`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    },
    sendCode(payload:any) {
      const api = useApi()
      return api(`${this.host}/api/user/v1/password-reset/send-code`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    },
    resetPassword(payload:any) {
      const api = useApi()
      return api(`${this.host}/api/user/v1/password-reset`, {
        method: 'POST',
        body: JSON.stringify(payload),
      })
    }
	},
})
